import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Printer, Loader2, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { API } from "../App";
import { useLang } from "../i18n/i18n";

export default function InvoicePage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const fmt = (v) => Number(v || 0).toLocaleString({ fr: "fr-CA", en: "en-CA", es: "es-ES" }[lang] || "fr-CA", { style: "currency", currency: "CAD" });

  useEffect(() => {
    axios
      .get(`${API}/invoices/${id}`)
      .then((r) => setInv(r.data))
      .catch(() => setInv(false))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <main className="min-h-screen flex items-center justify-center text-mutedc">
        <Loader2 size={26} className="animate-spin" />
      </main>
    );

  if (!inv)
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 px-5 text-center">
        <p className="font-display text-3xl text-cream">{t("inv.notFound")}</p>
        <Link to="/" className="rounded-full border border-cream/20 px-6 py-3 text-sm text-cream/80 hover:border-gold hover:text-goldlight transition-all" data-testid="invoice-back-home">
          {t("inv.back")}
        </Link>
      </main>
    );

  const date = inv.created_at
    ? new Date(inv.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA", { year: "numeric", month: "long", day: "numeric" })
    : "";

  return (
    <main className="min-h-screen flex items-center justify-center px-5 py-16 pt-[100px]" data-testid="invoice-page">
      <div className="w-full max-w-lg rounded-3xl border border-gold/25 bg-panel/80 p-8 sm:p-10 shadow-[0_30px_90px_rgba(0,0,0,0.6)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <Logo />
          <span
            className={`inline-block rounded-full border px-3.5 py-1.5 text-[11px] uppercase tracking-wider ${
              inv.status === "payee"
                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                : "border-gold/30 bg-gold/10 text-goldlight"
            }`}
            data-testid="invoice-status-badge"
          >
            {inv.status === "payee" ? t("inv.paid") : t("inv.pending")}
          </span>
        </div>

        <div className="hairline my-7" />

        <p className="text-xs uppercase tracking-[0.25em] text-mutedc" data-testid="invoice-number">{inv.number}</p>
        <h1 className="mt-2 font-display text-3xl text-cream">{t("inv.title")}</h1>

        <div className="mt-6 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-mutedc">{t("inv.client")}</span>
            <span className="text-cream" data-testid="invoice-client">{inv.client_name}</span>
          </div>
          {inv.client_contact && (
            <div className="flex justify-between">
              <span className="text-mutedc">{t("inv.contact")}</span>
              <span className="text-cream/80">{inv.client_contact}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-mutedc">{t("inv.date")}</span>
            <span className="text-cream/80">{date}</span>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-cream/10 bg-ink/50 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-mutedc">{t("inv.description")}</p>
          <p className="mt-2 text-sm leading-relaxed text-cream/85 whitespace-pre-line" data-testid="invoice-description">{inv.description}</p>
        </div>

        <div className="mt-6 flex items-baseline justify-between border-t border-gold/20 pt-6">
          <span className="text-sm uppercase tracking-[0.2em] text-mutedc">{t("inv.total")}</span>
          <span className="font-display text-4xl text-goldlight tabular" data-testid="invoice-total">{fmt(inv.amount)}</span>
        </div>

        <p className="mt-5 text-xs leading-relaxed text-mutedc">{t("inv.payable")}</p>

        <button
          onClick={() => window.print()}
          data-testid="invoice-print-btn"
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink transition-all hover:bg-goldlight"
        >
          {inv.status === "payee" ? <CheckCircle2 size={15} /> : <Printer size={15} />}
          {t("inv.print")}
        </button>
      </div>
    </main>
  );
}
